import { defineStore } from 'pinia'
import authService from '@/services/auth.service'
import type { Credentials } from '@/services/auth.service'
import { getAccessToken, clearTokens } from '@/api/tokens'
import { isLive } from '@/api/config'
import { createUser } from '@/entities/user'
import type { User } from '@/entities/user'

/**
 * Session state.
 *
 * `isAuthenticated` used to be hard-coded `true` next to a hard-coded Иван
 * Петров, which is why the deployed SPA let anyone straight into the
 * workspace. It is now derived from a real `/auth/me` round trip.
 *
 * In mock mode the fixture user is still resolved, but through the same
 * `bootstrap()` path — so the mocked and live flows exercise the same code
 * instead of diverging.
 */
const EMPTY_USER = createUser({})

/** The in-flight bootstrap, shared by every caller — see `bootstrap()`. */
let inFlight: Promise<void> | null = null

export interface AuthState {
  user: User
  isAuthenticated: boolean
  /** False until bootstrap() has settled; guards must wait on it. */
  ready: boolean
  loading: boolean
  error: unknown
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: EMPTY_USER,
    isAuthenticated: false,
    ready: false,
    loading: false,
    error: null,
  }),
  getters: {
    initials: (state) => state.user?.initials || '',
    fullName: (state) => state.user?.name || '',
    role: (state) => state.user?.role || '',
    workspace: (state) => state.user?.workspace || '',
  },
  actions: {
    /**
     * Restore the session on first load.
     *
     * Runs at most once per page load and is awaited by the router guard,
     * so a deep link to a protected page does not flash the login screen
     * for a user who is in fact signed in.
     */
    async bootstrap(): Promise<void> {
      if (this.ready) return
      /* Single-flight, not just `if (ready)`: `ready` is set *after* the
       * await, so two callers arriving together — a guard plus the redirect
       * target's guard, say — would both sail past the flag and fetch. */
      if (!inFlight) {
        inFlight = (async () => {
          /* No token means no session to restore. Skip the request rather
           * than spend a guaranteed 401 on every visit by a logged-out
           * user. */
          if (!isLive('auth') || getAccessToken()) await this.fetchMe()
          this.ready = true
        })().finally(() => {
          inFlight = null
        })
      }
      await inFlight
    },

    async fetchMe(): Promise<void> {
      this.loading = true
      try {
        const me = await authService.me()
        this.user = me ?? EMPTY_USER
        this.isAuthenticated = !!me
        this.error = null
      } catch (err) {
        /* A failed /auth/me is a dead session, not a transient blip: the
         * refresh interceptor has already had its turn by this point. */
        this.error = err
        this.user = EMPTY_USER
        this.isAuthenticated = false
        clearTokens()
      } finally {
        this.loading = false
      }
    },

    async signIn(credentials: Credentials) {
      const res = await authService.signIn(credentials)
      await this.fetchMe()
      if (!this.isAuthenticated) {
        throw this.error instanceof Error ? this.error : new Error('Sign-in failed')
      }
      this.ready = true
      return res
    },

    /**
     * Sign in by spending a reset or invitation link (W13.M3). The API sets
     * the password and returns a token pair; from there it is the ordinary
     * session path — `/auth/me`, then ready.
     */
    async signInWithLink(token: string, password: string) {
      const res = await authService.setPassword(token, password)
      await this.fetchMe()
      if (!this.isAuthenticated) {
        throw this.error instanceof Error ? this.error : new Error('Sign-in failed')
      }
      this.ready = true
      return res
    },

    async signOut(): Promise<void> {
      await authService.signOut()
      this.user = EMPTY_USER
      this.isAuthenticated = false
      this.error = null
    },

    /** Called by the HTTP layer when a refresh fails — session is over. */
    endSession(): void {
      clearTokens()
      this.user = EMPTY_USER
      this.isAuthenticated = false
    },
  },
})
